import React, { useRef, useEffect, useState } from "react";
import "./Analytics.css";
import { Chart, registerables } from "chart.js";
import Layout from "../components/layout";
import axios from "axios";
import { endpoint } from "../App";
import { toast, ToastContainer } from "react-toastify";
import { useData } from "../components/context";
import moment from "moment";

Chart.register(...registerables);

const filterCategories = [
  "plumbing",
  "masonry",
  "carpentry",
  "electrical",
  "other",
];

const Analytics = () => {
  const { complaints, fetchComplaints } = useData();
  const { complaintsOverTime, fetchComplaintsOverTime } = useData();
  const { avgResponseTime, fetchAvgResponseTime } = useData();
  const [chartsRendered, setChartsRendered] = useState(false);

  useEffect(() => {
    fetchComplaints(endpoint + "complaint/");
    fetchComplaintsOverTime(endpoint + "analytics/over-time/");
    fetchAvgResponseTime(endpoint + "analytics/avg-response-time/");
  }, []);

  const filteredComplaints = React.useMemo(() => {
    if (!complaints) return [];
    return complaints.filter((complaint) =>
      filterCategories.includes(complaint.category.toLowerCase())
    );
  }, [complaints]);

  useEffect(() => {
    const renderCharts = () => {
      const destroyChart = (chartId) => {
        const existingChart = Chart.getChart(chartId);
        if (existingChart) {
          existingChart.destroy();
        }
      };

      destroyChart("chart1");
      destroyChart("chart2");
      destroyChart("chart3");

      const complaintCounts = {};
      filteredComplaints.forEach((complaint) => {
        const category = complaint.category.toLowerCase();
        complaintCounts[category] = (complaintCounts[category] || 0) + 1;
      });

      const normalLabels = Object.keys(complaintCounts);
      const chart1Labels = normalLabels.map(label =>
        label.charAt(0).toUpperCase() + label.slice(1)
      );
      const chart1Data = Object.values(complaintCounts);
      new Chart("chart1", {
        type: "pie",
        data: {
          labels: chart1Labels,
          datasets: [
            {
              label: "Complaint Categories",
              data: chart1Data,
              backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#F62423"],
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              position: "right",
            },
          },
        },
      });

      const chart2Labels = complaintsOverTime.map((data) => 
        moment().month(data.month - 1).format("MMMM")
      );
      const chart2Data = complaintsOverTime.map((data) => data.complaint_count);
      new Chart("chart2", {
        type: "bar",
        data: {
          labels: chart2Labels,
          datasets: [
            {
              label: "Complaint Count",
              data: chart2Data,
              backgroundColor: [
                // Updated colors for chart2 (remove fill)
                "rgba(255, 99, 132, 0.8)", // Semi-transparent red
                "rgba(54, 162, 235, 0.8)", // Semi-transparent blue
                "rgba(255, 206, 86, 0.8)", // Semi-transparent yellow
                "rgba(75, 192, 192, 0.8)", // Semi-transparent green
                "rgba(153, 102, 255, 0.8)", // Semi-transparent purple
                "rgba(255, 159, 64, 0.8)",  // Semi-transparent orange
                "rgba(255, 204, 0, 0.8)",   // Semi-transparent gold
                "rgba(255, 69, 0, 0.8)",    // Semi-transparent orange-red
                "rgba(0, 255, 255, 0.8)",   // Semi-transparent cyan
                "rgba(128, 0, 128, 0.8)",   // Semi-transparent purple
                "rgba(0, 128, 0, 0.8)",     // Semi-transparent dark green
                "rgba(255, 192, 203, 0.8)"  // Semi-transparent pink
              ],
              borderColor: [
                // Consistent border colors for better visibility
                "rgba(255, 99, 132, 1)",
                "rgba(54, 162, 235, 1)",
                "rgba(255, 206, 86, 1)",
                "rgba(75, 192, 192, 1)",
                "rgba(153, 102, 255, 1)",
                "rgba(255, 159, 64, 1)",  
                "rgba(255, 204, 0, 1)",   
                "rgba(255, 69, 0, 1)",    
                "rgba(0, 255, 255, 1)",   
                "rgba(128, 0, 128, 1)",   
                "rgba(0, 128, 0, 1)",     
                "rgba(255, 192, 203, 1)"  
              ],
              borderWidth: 1,
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              display: false,
            },
          },
          scales: {
            yAxes: [
              {
                ticks: {
                  beginAtZero: true,
                },
              },
            ],
          },
        },
      });

      const chart3Labels = avgResponseTime.map((data) => data.category);
      const chart3Data1 = avgResponseTime.map(
        (data) => data.average_response_time
      );
      const chart3Data2 = avgResponseTime.map((data) => data.complaint_count);
      new Chart("chart3", {
        type: "bar",
        data: {
          labels: chart3Labels,
          datasets: [
            {
              label: "Average Response Time",
              data: chart3Data1,
              backgroundColor: "rgba(75, 192, 192, 0.2)",
              borderColor: "rgba(75, 192, 192, 1)",
              tension: 0.1,
            },
            {
              label: "Complaints Count",
              data: chart3Data2,
              backgroundColor: "rgba(54, 162, 232, 0.2)",
              borderColor: "rgba(54, 162, 232, 1)",
              tension: 0.1,
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              position: "right",
            },
          },
          scales: {
            xAxes: [
              {
                stacked: false, // Change to false for grouped bar chart
              },
            ],
            yAxes: [
              {
                stacked: false, // Change to false for grouped bar chart
              },
            ],
          },
        },
      });
    };

    if (
      filteredComplaints &&
      complaintsOverTime &&
      avgResponseTime &&
      filteredComplaints.length > 0 &&
      complaintsOverTime.length > 0 &&
      avgResponseTime.length > 0
    ) {
      renderCharts();
      setChartsRendered(true);
    }
  }, [filteredComplaints, complaintsOverTime, avgResponseTime]);

  const toastTimeOut = () => {
    setTimeout(() => {
      toast("Charts rendered successfully!");
    }, 10);
  };

  return (
    <>
      {/* <ToastContainer autoClose={4000} limit={1} /> */}
      {/* {toastTimeOut()} */}
      <Layout>
        <div className="Analytics">
          {/* Always render canvas elements */}
          <div className="chart-container">
            <h2>Complaint By Categories</h2>
            <canvas id="chart1"></canvas>
          </div>

          <div className="chart-container">
            <h2>Complaint Over Time</h2>
            <canvas id="chart2"></canvas>
          </div>

          <div className="chart-container">
            <h2>Average Response Time By Categories</h2>
            <canvas id="chart3"></canvas>
          </div>

          {/* Conditionally render additional chart components */}
          {/* {chartsRendered && ( */}
          <>{/* Render additional chart components here */}</>
          {/* )} */}
        </div>
      </Layout>
    </>
  );
};

export default Analytics;
